import { useEffect, useRef } from 'react';
import { useIdentity } from './contexts/IdentityContext.js';
import {
  type InboundMessage,
  type OutboundMessage,
  PROTOCOL_VERSION,
  isInboundMessage,
} from './protocol.js';

interface Props {
  /** Origin(s) allowed to send commands and receive events. Use a specific
   * origin in production. "*" disables origin validation — DEV ONLY. */
  allowedOrigin: string;
}

/**
 * Bidirectional postMessage bridge. Mounted only when the dashboard is
 * embedded in an iframe and the host opts in via mount({ postMessage: {...} }).
 *
 * Inbound: applies set-identity / clear-identity to IdentityContext.
 * Outbound: emits ready (once), identity-changed (on every change),
 *           and height (debounced via ResizeObserver) so the host can grow
 *           the iframe to match content without scrollbars.
 */
export function PostMessageBridge({ allowedOrigin }: Props) {
  const { identity, setIdentity } = useIdentity();
  const lastIdentityIdRef = useRef<string | null>(null);

  // Inbound listener.
  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      if (allowedOrigin !== '*' && ev.origin !== allowedOrigin) return;
      if (!isInboundMessage(ev.data)) return;
      const msg = ev.data as InboundMessage;
      switch (msg.type) {
        case 'losan-dashboard/set-identity':
          if (msg.payload?.agentId) {
            // Host-injected identities are always regular agents; admin is a
            // demo-only role exercised through the internal login.
            const next = msg.payload.token
              ? { agentId: msg.payload.agentId, role: 'agent' as const, token: msg.payload.token }
              : { agentId: msg.payload.agentId, role: 'agent' as const };
            setIdentity(next);
          }
          return;
        case 'losan-dashboard/clear-identity':
          setIdentity(null);
          return;
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [allowedOrigin, setIdentity]);

  // "ready" — fired once on mount.
  useEffect(() => {
    postToParent({ type: 'losan-dashboard/ready', payload: { version: PROTOCOL_VERSION } }, allowedOrigin);
  }, [allowedOrigin]);

  // "identity-changed" — every time identity transitions.
  useEffect(() => {
    const id = identity?.agentId ?? null;
    if (id === lastIdentityIdRef.current) return;
    lastIdentityIdRef.current = id;
    postToParent({ type: 'losan-dashboard/identity-changed', payload: { agentId: id } }, allowedOrigin);
  }, [identity, allowedOrigin]);

  // "height" — emits when document body height changes.
  useEffect(() => {
    let lastHeight = -1;
    const emit = () => {
      const h = document.documentElement.scrollHeight;
      if (h === lastHeight) return;
      lastHeight = h;
      postToParent({ type: 'losan-dashboard/height', payload: { height: h } }, allowedOrigin);
    };
    emit();
    const obs = new ResizeObserver(emit);
    obs.observe(document.body);
    return () => obs.disconnect();
  }, [allowedOrigin]);

  return null;
}

function postToParent(msg: OutboundMessage, targetOrigin: string) {
  if (window.parent === window) return; // not in iframe
  try {
    window.parent.postMessage(msg, targetOrigin);
  } catch {
    /* swallow — bridge is best-effort */
  }
}
